import L from 'leaflet';
import { useEffect, useMemo } from 'react';
import { MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap } from 'react-leaflet';
import type { TrackPoint } from '../../../shared/types/api';
import { initialTileProvider } from './tileProvider';
import { splitTrailAtAntimeridian } from './trail';

const previousIcon = L.divIcon({
  className: 'trail-marker-wrap',
  html: '<span class="trail-marker" aria-hidden="true"></span>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const latestIcon = L.divIcon({
  className: 'latest-marker-wrap',
  html: '<span class="latest-marker"><span>★</span></span>',
  iconSize: [42, 42],
  iconAnchor: [21, 21],
});

function TrailViewport({ points, centerSignal }: { points: TrackPoint[]; centerSignal: number }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    const bounds = L.latLngBounds(points.map((point) => [point.latitude, point.longitude]));
    map.fitBounds(bounds, { padding: [36, 36], maxZoom: 7, animate: centerSignal > 0 });
  }, [centerSignal, map, points]);
  return null;
}

export function TrackMap({ points, centerSignal }: { points: TrackPoint[]; centerSignal: number }) {
  const segments = useMemo(() => splitTrailAtAntimeridian(points), [points]);
  const latest = points.at(-1)!;

  return (
    <div className="map-frame">
      <MapContainer
        center={[latest.latitude, latest.longitude]}
        zoom={5}
        scrollWheelZoom
        worldCopyJump
        className="trail-map"
      >
        <TileLayer
          url={initialTileProvider.url}
          attribution={initialTileProvider.attribution}
          maxZoom={initialTileProvider.maxZoom}
        />
        {segments.map((segment, index) => (
          <Polyline
            key={`${segment[0]?.join('-') ?? index}-${index}`}
            positions={segment}
            pathOptions={{ color: '#ff7557', weight: 5, opacity: 0.88, lineCap: 'round' }}
          />
        ))}
        {points.slice(0, -1).map((point) => (
          <Marker key={point.id} position={[point.latitude, point.longitude]} icon={previousIcon}>
            <Tooltip>{new Date(point.trackedAt).toLocaleDateString()}</Tooltip>
          </Marker>
        ))}
        <Marker
          position={[latest.latitude, latest.longitude]}
          icon={latestIcon}
          zIndexOffset={1000}
        >
          <Tooltip permanent direction="top" offset={[0, -20]}>
            Latest trail point
          </Tooltip>
        </Marker>
        <TrailViewport points={points} centerSignal={centerSignal} />
      </MapContainer>
    </div>
  );
}
